import React from 'react';
import { IActivity } from '../../data-models';
import { isFailedUpload, isTrackedUpload } from '../../utils/upload-activities';
import { useUploadActivities } from '../use-data';

const isTrackedFailure = (activity: IActivity) =>
  isTrackedUpload(activity) && isFailedUpload(activity);

/**
 * Tracks uploads that fail while the caller is mounted. Failures already in
 * the first activities loaded aren't reported.
 * @returns The failures not yet dismissed, and a function to dismiss them
 */
export const useNewUploadFailures = () => {
  const { data } = useUploadActivities();
  const activities = data?.activities;
  const [seenIds, setSeenIds] = React.useState<ReadonlySet<string>>();
  const [newFailures, setNewFailures] = React.useState<IActivity[]>([]);

  // compared during render, rather than in an effect, so the toast shows in
  // the same render the failure arrives
  if (activities) {
    const failures = activities.filter(isTrackedFailure);

    if (!seenIds) {
      setSeenIds(new Set(failures.map((failure) => failure.activityId)));
    } else {
      const fresh = failures.filter(
        (failure) => !seenIds.has(failure.activityId),
      );
      if (fresh.length) {
        setSeenIds(
          new Set([...seenIds, ...fresh.map((failure) => failure.activityId)]),
        );
        setNewFailures((current) => [...current, ...fresh]);
      }
    }
  }

  const dismiss = React.useCallback(() => setNewFailures([]), []);

  return { newFailures, dismiss };
};
